import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionPageSignerVelocityExperiment'];

type Params = {
	experiment: Experiment;
	signed: boolean;
};

/**
 * Treatment is deferred to the front-end (`useTreatSignerVelocityExperiment`), so `treated` is always
 * forced to `false` here — the middle-end can return `treated: true` for a returning session even with
 * `treat: false`.
 *
 * Deferral is not a preference: the experiment only counts pageviews above the daily ignition floor, which
 * needs `petition.dailySignatureCount` from pageData. This normalizer runs in the pageUserData path and
 * has no access to it, so the floor can only be evaluated at render time.
 */
export function normalizePetitionPageSignerVelocityExperiment({ experiment, signed }: Params): Experiment {
	if (!shouldRunExperiment({ experiment, signed })) {
		return forceControlOffScope(experiment);
	}

	return { ...experiment, treated: false };
}

/**
 * Resolves an out-of-scope visitor to control **and marks it as a non-assignment**.
 *
 * The `fallback` flag is the load-bearing part: `fallback || override` is the platform's only signal for
 * "not a real assignment", and it's what suppresses `$exposure`. A bare `{ variation: 'control' }` would be
 * byte-identical to genuine control bucketing, so an out-of-scope visitor could be treated and exposed as
 * control, inflating that arm undetectably.
 */
function forceControlOffScope(experiment: Experiment): Experiment {
	return {
		type: experiment.type,
		experimentName: experiment.experimentName,
		config: experiment.config,
		variation: 'control',
		treated: false,
		fallback: true,
		// Keep a real upstream reason (e.g. `disabled` from the kill switch); claim `not-enrolled` only when
		// the exclusion is ours.
		fallbackReason: experiment.fallback ? experiment.fallbackReason : 'not-enrolled',
		// `override` is deliberately not carried through: the resolved-experiment type treats it and
		// `fallback` as mutually exclusive, and the forced control is our scope decision, not the override's.
	} as Experiment;
}

function shouldRunExperiment({ experiment, signed }: Params): boolean {
	// `signed` is false for every guest — getPetitionViewerInfo short-circuits without a `currentUserId`. So
	// this excludes known signers only; symmetric across arms, so it can't bias the comparison.
	return isEnabledByFC(experiment) && !signed;
}

/** An `enabledIf` failure surfaces as `fallback: true`, not a distinct sentinel. */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
